import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  OutcomeType,
  UserOutcomeDetail,
} from '@app/outcomes/outcomes-api.model';
import { AchievementsModalService } from '@app/profile/components/profile-overview/achievements/achievements-modal/achievements-modal.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { TagsApi } from '@app/tags/tag-api.model';
import { TranslateService } from '@ngx-translate/core';
import { Profile } from '@app/profile/profile.model';
import { AuthService } from '@app/shared/services/auth.service';
import {
  CollectionItemType,
  ProfileCollectionItem,
  ProfileCollectionModel,
} from '@app/profile/profile-api.model';
import { SearchFacet } from '@app/search/search-api.model';
import { InputsService } from '@app/inputs/services/inputs.service';
import { isOutcomeTile } from '@app/profile/components/profile-collection/profile-collection-reactive-store/utils';
import { FocusStackService } from '@app/shared/services/focus-stack.service';
import { AchievementsService } from '@app/profile/components/profile-overview/achievements/achievements.service';
import { ACHIEVEMENT_TYPES } from '@app/profile/components/profile-overview/profile-overview.service';
import { getEmptyTileCount } from '@app/profile/components/profile-overview-v2/profile-overview.utils';
import { Params } from '@angular/router';

@Component({
  selector: 'dgx-profile-overview-achievements',
  templateUrl: './achievements.component.html',
  styleUrls: ['./achievements.component.scss'],
})
export class ProfileOverviewAchievementsComponent
  extends SubscriberBaseDirective
  implements OnInit, OnChanges
{
  // Bindings
  @Input() public profile: Profile;
  @Input() public ownerIsViewing: boolean;
  @Input() public achievementDisplayLimit: number = 3;
  @Input() public showViewAll: boolean = true;
  @Input() public showAdd: boolean = true;
  @Input() public useCarousel: boolean = true;
  @Input() public useShowMore: boolean;
  @Input() public tag: TagsApi.Tag;
  // This can be used broadly to show all achievements, or achievements for a specific tag
  // if a tag is required, make sure to wait for the tag to be available before fetching achievements
  @Input() public requiresTag: boolean;
  @Input() public isPageLoading: boolean;
  @Input() public useLoadingTemplate: boolean;
  @Input() public pageType: string;
  @Input() public isMobile: boolean = false;
  @Input() public cardRowLimit: number = 3; // only in use when useCarousel is false

  @Output() public achievementsLoading = new EventEmitter<{
    type: string;
    status: boolean;
  }>();

  @ViewChild('addMenuButton') public menuButtonRef: TemplateRef<any>;

  public authUser = this.authService.authUser;
  public isEmpty: boolean;
  public achievements: ProfileCollectionItem<UserOutcomeDetail>[];
  public hasMoreItems: boolean;
  public facets: SearchFacet[];

  public titleLimit = 25;
  public viewAllUrlQueryParams: Params;
  public emptyTiles: number[] = [];
  public isLoggedIn = false;
  public currentStartIndex: number = 0;
  public currentEndIndex: number;
  public sectionLoading: boolean = true;
  public itemTypes: CollectionItemType[];

  public i18n = this.translateService.instant([
    'Core_Add',
    'Core_ViewAll',
    'Core_ViewDetails',
    'UserController_Achievements',
    'dgProfileOverview_OverviewNotProvidedTitle',
    'Core_Accomplishment',
    'Core_Award',
    'Core_Badge',
    'Core_Certificate',
    'Core_Degree',
    'dgProfileOverview_AddAchievements',
    'UserController_Type',
    'Core_CertificatesDisplayName',
    'Core_AwardsDisplayName',
    'Core_BadgesDisplayName',
    'Core_AccomplishmentsDisplayName',
    'Core_DegreesDisplayName',
    'Core_AllTypesDisplayName',
    'Core_ShowMore',
  ]);

  public menuConfig: MenuViewModel[] = [
    {
      title: this.i18n.Core_Accomplishment,
      defaultAction: (event) => this.showAddModal('Accomplishment', event),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Award,
      defaultAction: (event) => this.showAddModal('Award', event),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Certificate,
      defaultAction: (event) => this.showAddModal('Certificate', event),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Degree,
      defaultAction: (event) => this.showAddModal('Degree', event),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Badge,
      defaultAction: (event) => this.showAddModal('Badge', event),
      preventRefocus: true,
    },
  ];

  public titleTranslationMap = {
    Accomplishment: this.i18n.Core_Accomplishment,
    Award: this.i18n.Core_Award,
    Certificate: this.i18n.Core_Certificate,
    Degree: this.i18n.Core_Degree,
    Badge: this.i18n.Core_Badge,
  };

  constructor(
    private authService: AuthService,
    private translateService: TranslateService,
    private achievementsModalService: AchievementsModalService,
    private inputsService: InputsService,
    private focusStackService: FocusStackService,
    private achievementsService: AchievementsService
  ) {
    super();

    this.inputsService.inputModify
      .pipe(this.takeUntilDestroyed())
      .subscribe((contentType) => {
        if (isOutcomeTile(contentType)) {
          this.updateAchievements(true);
          this.itemTypes = this.getItemTypes();
        }
      });
  }

  public ngOnInit(): void {
    this.setLoadingState(true);
    this.getInitialAchievements();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.pageType || changes?.tag) {
      this.getInitialAchievements();
    }
  }

  public getInitialAchievements(): void {
    if (this.requiresTag && !this.tag) {
      return;
    }

    this.achievements = [];
    this.achievementsService
      .getMostRecentAchievementsModel(
        this.profile.id,
        0,
        this.achievementDisplayLimit,
        this.tag?.name,
        ''
      )
      .pipe(this.takeUntilDestroyed())
      .subscribe(
        (achievementsModel: ProfileCollectionModel<UserOutcomeDetail>) => {
          this.achievements =
            this.extractAchievementItemsFromModel(achievementsModel);
          this.facets = achievementsModel.facets;
          this.hasMoreItems = this.getHasMoreItems(
            achievementsModel.hasMoreItems
          );
          this.itemTypes = this.getItemTypes();
          this.setup();
        }
      );
  }

  public get collectionUrl(): string {
    const ownerUrl = `/${this.profile.vanityUrl}`;

    this.viewAllUrlQueryParams = { classifier: 'achievements' };

    return `/profile${ownerUrl}/collection`;
  }

  public showAddModal(outcomeType: OutcomeType, event: Event): void {
    this.achievementsService
      .addUserOutcome(this.menuButtonRef.elementRef, outcomeType)
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.focusStackService.pop();
      });
  }

  public setup(): void {
    this.isLoggedIn = !!this.authUser;
    this.isEmpty = !this.achievements.length;
    if (!this.isMobile) {
      this.emptyTiles = getEmptyTileCount(
        this.achievements.length,
        this.cardRowLimit
      );
    }

    this.currentEndIndex = this.achievements.length - 1;
    this.setLoadingState(false);
  }

  public trackByCollectionId(
    index: number,
    achievement: ProfileCollectionItem<UserOutcomeDetail>
  ) {
    return achievement.itemId;
  }

  public showDetails(achievement: UserOutcomeDetail): void {
    this.achievementsModalService
      .showAchievementModal(
        this.profile.id,
        achievement.userOutcomeId,
        this.ownerIsViewing,
        !!achievement.imageUrl
      )
      .pipe(this.takeUntilDestroyed())
      .subscribe();
  }

  public loadMoreAchievements(index: number): void {
    if (index === this.currentEndIndex && this.hasMoreItems) {
      this.sectionLoading = true;

      this.currentStartIndex = this.currentEndIndex + 1;
      this.currentEndIndex =
        this.currentStartIndex + this.achievementDisplayLimit;
      this.updateAchievements();
    }
  }

  public trackByTypeName(index: number, itemType: CollectionItemType): string {
    return itemType.name;
  }

  public getHasMoreItems(returnedHasMoreItems: boolean): boolean {
    // This is a temporary workaround for a BE bug where hasMoreItems == true if the totalCount is
    // greater than OR EQUAL to the number of items returned, resulting in the showMore / scroll for more
    // being enabled when there are no more items to fetch
    const totalCount =
      this.facets.find((f) => f.id === 'Category')?.values[0]?.count || 0;
    return returnedHasMoreItems && totalCount > this.achievements.length;
  }

  private setLoadingState(status: boolean): void {
    this.sectionLoading = status;
    this.achievementsLoading.emit({ type: 'achievements', status });
  }

  private getTotalCount(): number {
    let count = 0;
    for (const typeName of ACHIEVEMENT_TYPES) {
      count += this.facets[0].values.find((f) => f.id === typeName)?.count || 0;
    }
    return count;
  }

  private getItemTypes(): CollectionItemType[] {
    return [
      {
        count: this.getTotalCount(),
        displayName: this.i18n.Core_AllTypesDisplayName,
        name: null,
        type: null,
      },
      {
        count:
          this.facets[0].values.find((f) => f.name === 'Degree')?.count || 0,
        displayName: this.i18n.Core_DegreesDisplayName,
        name: 'userdegree',
        type: 'Achievement',
      },
      {
        count:
          this.facets[0].values.find((f) => f.name === 'Accomplishment')
            ?.count || 0,
        displayName: this.i18n.Core_AccomplishmentsDisplayName,
        name: 'useraccomplishment',
        type: 'Achievement',
      },
      {
        count:
          this.facets[0].values.find((f) => f.name === 'Badge')?.count || 0,
        displayName: this.i18n.Core_BadgesDisplayName,
        name: 'userbadge',
        type: 'Achievement',
      },
      {
        count:
          this.facets[0].values.find((f) => f.name === 'Award')?.count || 0,
        displayName: this.i18n.Core_AwardsDisplayName,
        name: 'useraward',
        type: 'Achievement',
      },
      {
        count:
          this.facets[0].values.find((f) => f.name === 'Certificate')?.count ||
          0,
        displayName: this.i18n.Core_CertificatesDisplayName,
        name: 'usercertificate',
        type: 'Achievement',
      },
    ];
  }

  private extractAchievementItemsFromModel(
    model: ProfileCollectionModel<UserOutcomeDetail>
  ): ProfileCollectionItem<UserOutcomeDetail>[] {
    return model.items.reduce(
      (allItems, item: ProfileCollectionItem<UserOutcomeDetail>) => {
        allItems.push(item);
        return allItems;
      },
      []
    );
  }

  private updateAchievements(replaceAchievements = false): void {
    this.setLoadingState(true);
    const ownerKey = this.profile.id;
    if (replaceAchievements) {
      // getting a new set of achievements due to adding / deleting / filtering so set the start index to 0
      this.currentStartIndex = 0;
    }
    this.achievementsService
      .getMostRecentAchievementsModel(
        ownerKey,
        this.currentStartIndex,
        this.achievementDisplayLimit,
        this.tag?.name,
        ''
      )
      .pipe(this.takeUntilDestroyed())
      .subscribe(
        (achievementsModel: ProfileCollectionModel<UserOutcomeDetail>) => {
          const newAchievements =
            this.extractAchievementItemsFromModel(achievementsModel);

          if (this.currentStartIndex === 0 || replaceAchievements) {
            this.achievements = newAchievements;
          } else {
            this.achievements.push(...newAchievements);
          }
          this.facets = achievementsModel.facets;
          this.hasMoreItems = this.getHasMoreItems(
            achievementsModel.hasMoreItems
          );
          this.setup();
        }
      );
  }
}
